'use client';
import { useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Control, useController } from 'react-hook-form';
import { FiAlertCircle, FiCheckCircle, FiEye, FiEyeOff, FiXCircle } from 'react-icons/fi';
import { tv } from 'tailwind-variants';
import ErrorLabel from './ErrorLabel';
import { bounceTransition } from '@/animation/transitions';

interface CxInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  placeholder?: string;
  hasError?: boolean;
  bottomText?: string;
  icon?: React.ReactNode;
  success?: boolean;
  disabled?: boolean;
  getInputRef?: (ref?: HTMLInputElement | null) => void;
  showClearButton?: boolean;
  onClear?: () => void;
  disableAutocomplete?: boolean;
}

const input = tv({
  base: 'text-gray-01 bg-cx-black p-4 rounded-sm flex flex-row items-center gap-2',
  variants: {
    hasError: {
      true: 'border border-support-error',
    },
    success: {
      true: 'border border-support-success',
    },
    isDate: { true: '[&>input]:cursor-pointer' },
  },
  defaultVariants: {
    hasError: false,
    success: false,
  },
});

const bottomMessage = tv({
  base: 'text-xs',
  variants: {
    hasError: {
      true: 'text-support-error',
    },
    success: {
      true: 'text-support-sucess',
    },
  },
  defaultVariants: {
    hasError: false,
    success: false,
  },
});

const container = tv({
  base: 'flex w-full flex-col gap-2',
  variants: {
    isDate: { true: 'cursor-pointer' },
  },
});

export function CxInput({
  label,
  placeholder,
  hasError,
  bottomText,
  icon,
  success,
  disabled,
  type,
  getInputRef,
  showClearButton,
  onClear,
  className,
  disableAutocomplete = true,
  ...props
}: CxInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDate = type === 'date';
  const isPassword = type === 'password';

  const [localType, setLocalType] = useState(type);

  const hasValue =
    typeof props.value === 'string'
      ? props.value.length > 0
      : typeof props.value === 'number'
        ? true
        : false;

  const handleOnTogglePassword = () => {
    setLocalType((old) => (old === 'text' ? 'password' : 'text'));
  };

  return (
    <div
      className={container({ isDate })}
      onClick={isDate ? () => inputRef.current?.showPicker() : undefined}
    >
      <label className="text-gray-01 text-xs">{label}</label>

      <div className={input({ isDate, hasError, success })}>
        {hasError ? (
          <FiAlertCircle className="text-support-error" size={20} />
        ) : success ? (
          <FiCheckCircle className="text-support-success" size={20} />
        ) : (
          icon
        )}

        <input
          {...props}
          type={isPassword ? localType : type}
          ref={(e) => {
            getInputRef && getInputRef(e);
            inputRef.current = e;
          }}
          disabled={disabled}
          className="text-gray-01 placeholder:text-gray-02 bg-cx-black w-full outline-hidden read-only:cursor-not-allowed read-only:opacity-50"
          placeholder={placeholder}
          autoComplete={disableAutocomplete ? 'off' : props.autoComplete}
          spellCheck={disableAutocomplete ? false : props.spellCheck}
          inputMode={props.inputMode}
          data-lpignore={disableAutocomplete ? 'true' : undefined} /* 1Password, LastPass, etc. */
          data-form-type={disableAutocomplete ? 'other' : undefined} /* Apple, Bitwarden, etc. */
        />

        {showClearButton && hasValue && !isPassword && (
          <button
            type="button"
            className="ml-auto"
            onClick={(e) => {
              e.stopPropagation();
              onClear && onClear();
            }}
          >
            <FiXCircle className="hover:text-brand-color-green text-gray-02 transition-colors duration-200 ease-in-out" />
          </button>
        )}

        {isPassword && (
          <motion.button
            type="button"
            className="text-gray-02 ml-auto"
            onClick={handleOnTogglePassword}
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.05 }}
          >
            {localType === 'password' ? <FiEyeOff /> : <FiEye />}
          </motion.button>
        )}
      </div>

      <p className={bottomMessage({ hasError, success })}>{bottomText}</p>
    </div>
  );
}

interface CxInputFormProps extends CxInputProps {
  control: Control;
}

export function CxInputForm({ control, ...rest }: CxInputFormProps) {
  const { field, fieldState } = useController({ control, name: rest?.name || '' });

  return (
    <div className="flex flex-col">
      <CxInput
        {...rest}
        value={field.value || ''}
        getInputRef={field.ref}
        name={field.name}
        onChange={field.onChange}
      />
      {fieldState?.error && <ErrorLabel>{fieldState?.error?.message}</ErrorLabel>}
    </div>
  );
}
